"use client";

import { getApproxDPR, getQualityBasedOnNetwork, imageKitLoader, Typography } from "@aph/ui-lib-humanx";
import React, { useEffect } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import CategoryComponentMapping from "@/components/CategoryPage/categoryComponentMapping";
import NavBar from "@/components/Header/NavBar";
import {
  COMMON_COMPONENT_TYPES,
  HOME_PAGE_TITLE,
  NON_ONBOARDED_STATUS,
  ONBOARDED_HOME_PAGE_TITLE,
  ONBOARDING_STATUS,
} from "@/helpers/constants";
import routes from "@/helpers/router";
import { useSyncCategoryData } from "@/components/CategoryPage/syncCategoryData";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { getCookie } from "@/helpers/cookies";
import { updateUserOnboarded } from "@/redux/slice/auth/slice";
import Image from "@/components/Image";
import { isBrowser } from "@/libs/helpers";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import signedInStateSelector from "@/redux/slice/auth/selectors/signedInState";
import currentPatientSelector from "@/redux/slice/auth/selectors/currentPatient";
import { selectAllAddresses } from "@/redux/slice/location/selectors/addressDetails";
import { pageViewEvent } from "@/helpers/TSDEvents";
import { setReferer } from "@/redux/slice/global/slice";
import { getPatientAge } from "@/redux/slice/auth/utils/getPatientAge";
import styles from "./CategoryPage.module.scss";

interface BreadcrumbsInterface {
  label: string;
  url: string;
}

const CategoryPage = ({
  categoryData: categoryInfoObject,
  categorySlug,
  userOnBoarded,
  ua,
  referer,
  productInfo,
  currentSpeciality,
  doctorsArray,
  segmentInfo,
  diagnosticsArray,
  isPharma = false,
}) => {
  const segmentInformation = segmentInfo ? JSON.parse(segmentInfo?.value) : null;

  useSyncCategoryData({ categoryInfo: categoryInfoObject, ua, categorySlug, userOnBoarded });
  const dispatch = useAppDispatch();
  const signedInStatus = useAppSelector(signedInStateSelector);
  const currentPatientData = useAppSelector(currentPatientSelector);
  const currentAddresses = useAppSelector(selectAllAddresses);

  const generateBreadcrumbs = () => {
    const categoryData = categoryInfoObject?.data?.[0]?.attributes;
    const breadCrumbs: [BreadcrumbsInterface] = [
      {
        label: userOnBoarded ? ONBOARDED_HOME_PAGE_TITLE : HOME_PAGE_TITLE,
        url: routes.home,
      },
    ];

    if (categoryData?.CategoryName) {
      breadCrumbs.push({
        label: categoryData?.CategoryName,
        url: routes.category(categoryData?.Slug),
      });
    }

    return breadCrumbs;
  };

  useEffect(() => {
    if (getCookie("segmentInfo")) {
      dispatch(updateUserOnboarded(true));
    }
  }, [categorySlug]);

  useEffect(() => {
    dispatch(setReferer(referer));

    if (signedInStatus !== SignedinState.UNRESOLVED) {
      const city = currentAddresses?.length > 0 ? currentAddresses[0].city : "";
      const isUserLoggedin = signedInStatus === SignedinState.SIGNEDIN;

      pageViewEvent({
        page: "category",
        is_loggedin: isUserLoggedin ? true : false,
        momverse_stage: userOnBoarded ? (ONBOARDING_STATUS[segmentInformation?.type] ?? "") : NON_ONBOARDED_STATUS,
        age: getPatientAge(currentPatientData),
        firstname: isUserLoggedin && currentPatientData?.firstName ? currentPatientData?.firstName : "",
        category_name: categoryInfoObject?.data?.[0]?.attributes?.CategoryName,
        city,
        nav_src: referer || "direct",
      });
    }
  }, [signedInStatus, currentAddresses]);

  const breadCrumbsArray: [BreadcrumbsInterface] = generateBreadcrumbs();
  const categorySections = categoryInfoObject?.data?.[0]?.attributes?.CategoryContent;
  const categoryDetail = categoryInfoObject?.data?.[0]?.attributes;

  const navBarSection = categorySections?.find(
    (section: any) => section.ComponentType === COMMON_COMPONENT_TYPES.NavBar
  );

  const quality = isBrowser() ? getQualityBasedOnNetwork() : 80;

  return (
    <>
      <section>
        <link
          rel="preload"
          href={imageKitLoader({
            format: "webp",
            quality,
            src: categoryDetail?.CategoryBanner?.data?.attributes?.url,
            dpr: getApproxDPR(),
            width: "400",
          })}
          fetchPriority="high"
          as="image"
        />
      </section>
      {navBarSection?.id && (
        <NavBar
          navBarData={navBarSection}
          userOnBoarded={userOnBoarded}
          userAgent={ua}
          pathname={routes.category(categorySlug)}
        />
      )}
      <div className={`container breadcrumb`}>
        <Breadcrumbs options={breadCrumbsArray} />
      </div>
      <div className={`${styles.firstFoldWrapper}`}>
        <Image
          src={categoryDetail?.CategoryBanner?.data?.attributes?.url}
          className={styles.bgImage}
          alt={categoryDetail?.CategoryBanner?.data?.attributes?.alternativeText || categoryDetail?.CategoryName}
          eagerLoad={true}
          width={ua?.isDesktop ? 750 : 320}
          height={ua?.isDesktop ? 208 : 125}
        />
        <div className={`container ${styles.headerSection}`}>
          <Typography component="h1" className={`${styles.title}`}>
            {categoryDetail?.CategoryName}
          </Typography>
        </div>
      </div>
      {categorySections?.map((section: any, index: number) => {
        return (
          <React.Fragment key={index}>
            <>
              <CategoryComponentMapping
                data={section}
                isPharma={isPharma}
                options={{
                  userOnBoarded,
                  categorySlug,
                  productInfo,
                  currentSpeciality,
                  doctorsArray,
                  index,
                  diagnostics: diagnosticsArray,
                }}
              />
            </>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default CategoryPage;
